<template>
  <div class="mc-modal-fleet__item-false">
    <div v-if="!popup.formOpen" class="d-flex justify-content-between align-items-center">
      <span class="ms-2">
        <template v-if="popup.alert.isFalseAlert">
          <p class="font-size-13">
            <i class="mc-fa icon-fill_bell-off"></i>
            {{ $t('notification.text.notificationFalseAlert') }}
          </p>
        </template>
        <template v-else-if="popup.alert?.isResolved">
          <p>
            <i class="mc-fa icon-fill_checkmark-circle"></i>
            {{ $t('notification.action.notificationChecked') }}
          </p>
        </template>
        <template v-else>
          <span class="font-size-13">
            {{ `${$t('notification.text.hasBeenChecked')} ?` }}
          </span>
        </template>
      </span>
      <div v-if="popup.alert.isFalseAlert">
        <McButton
          :data-me="`${dataMePrefix}-btn-recheck`"
          variant="secondary"
          :text="$t('notification.text.checkAgain')"
          size="sm"
          :disabled="isPending('recheck')"
          :loading="isPending('recheck')"
          @click="handleAction('recheck')"
        />
      </div>
      <div v-else-if="popup.alert.isResolved">
        <McButton
          :data-me="`${dataMePrefix}-btn-check-again`"
          variant="secondary"
          :text="$t('notification.text.checkAgain')"
          size="sm"
          :disabled="isPending('resolved')"
          :loading="isPending('resolved')"
          @click="handleAction('resolved')"
        />
      </div>
      <div v-else>
        <McButton
          :data-me="`${dataMePrefix}-btn-resolve`"
          icon="icon-outline_checkmark-circle"
          variant="outline-success"
          :text="$t('global.yes')"
          size="sm"
          :disabled="popup.isResolving || isPending('resolve')"
          :loading="popup.isResolving || isPending('resolve')"
          @click="handleAction('resolve')"
        />
        <McButton
          :data-me="`${dataMePrefix}-btn-open-form`"
          icon="icon-outline_bell-off"
          variant="outline-danger"
          :text="$t('notification.text.wrongNotification')"
          size="sm"
          class="ms-2"
          :disabled="isPending('open-form')"
          @click="handleAction('open-form')"
        />
      </div>
    </div>
    <div v-else>
      <label class="font-size-13">
        {{ $t('notification.text.whyFalseAlert') }} ?
        <span class="text-danger">*</span>
      </label>
      <FormulateForm v-model="localForm" @input="handleFormInput">
        <FormulateInput
          v-model="localForm.reason"
          type="textarea"
          name="reason"
          :data-me="`${dataMePrefix}-input-reason`"
          :placeholder="$t('notification.text.fillFalseAlert')"
          validation="max:300,length"
          :validation-name="$t('notification.text.otherReasons')"
          error-behavior="live"
        />
        <div class="mc-notification-alert__false-suggest mt-1">
          <McButton
            :data-me="`${dataMePrefix}-btn-no-event-reason`"
            variant="secondary"
            size="sm"
            :text="$t('notification.text.notValid')"
            :disabled="isPending('set-reason')"
            @click="handleAction('set-reason', noEventReason)"
          />
          <McButton
            :data-me="`${dataMePrefix}-btn-other-reason`"
            variant="secondary"
            size="sm"
            class="ms-1"
            :text="$t('notification.text.otherReason2')"
            :disabled="isPending('set-reason')"
            @click="handleAction('set-reason', otherEventReason)"
          />
        </div>
      </FormulateForm>
      <div class="d-flex justify-content-between gap-1 mt-2">
        <McButton
          :data-me="`${dataMePrefix}-btn-cancel`"
          :text="$t('global.action.cancel')"
          variant="secondary"
          size="sm"
          block
          :disabled="isPending('cancel')"
          @click="handleAction('cancel')"
        />
        <McButton
          :data-me="`${dataMePrefix}-btn-save`"
          :text="$t('global.action.save')"
          variant="primary"
          size="sm"
          block
          :disabled="isPending('false-alert')"
          :loading="isPending('false-alert')"
          @click="handleAction('false-alert')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import McButton from '@/components/McButton'

export default {
  name: 'FalseAlertActionCard',

  components: { McButton },

  props: {
    popup: {
      type: Object,
      required: true,
    },
    actionHandlers: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      localForm: {
        reason: this.popup.form?.reason ?? null,
        others: this.popup.form?.others ?? null,
      },
      pendingEvents: {},
      noEventReason: 'Tidak ada kejadian',
      otherEventReason: 'Kejadian Lainnya',
    }
  },

  computed: {
    dataMePrefix() {
      return this.$attrs['data-me'] || 'alert-center-false-alert-card'
    },
  },

  watch: {
    'popup.form': {
      handler(form) {
        this.localForm = {
          reason: form?.reason ?? null,
          others: form?.others ?? null,
        }
      },
      deep: true,
    },
  },

  methods: {
    isPending(eventName) {
      return Boolean(this.pendingEvents[eventName])
    },
    async handleAction(eventName, payload) {
      if (this.pendingEvents[eventName]) return

      this.$set(this.pendingEvents, eventName, true)

      try {
        const handler = this.actionHandlers[eventName]
        if (handler) {
          await handler(payload)
        } else {
          this.$emit(eventName, payload)
        }
      } finally {
        this.clearPending(eventName)
      }
    },
    clearPending(eventName) {
      if (!this.pendingEvents[eventName]) return

      this.$delete(this.pendingEvents, eventName)
    },
    handleFormInput(value) {
      this.localForm = {
        reason: value?.reason ?? null,
        others: value?.others ?? null,
      }
      this.$emit('form-change', this.localForm)
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-modal-fleet__item-false {
  :deep(.formulate-input) {
    padding: 0;
  }

  .icon-fill_bell-off {
    vertical-align: middle;
    margin-right: 3px;

    &:before {
      color: $red;
    }
  }

  .ms-2 {
    .icon-fill_checkmark-circle {
      vertical-align: bottom;

      &:before {
        color: $green;
      }
    }
  }
}

:deep(.mc-notification-alert__false-suggest) {
  .mc-btn__sm {
    padding: 2px 5px;
    font-size: 9px;
    background-color: $cyan-100;
    color: $cyan-400;
    border: 0;

    &:hover {
      background-color: $cyan-100;
      color: $cyan-400;
    }
  }
}
</style>
